import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { listCenterTickets } from '../../services/tickets'
import { listCenterTicketTypes, addTicketType, updateTicketType, deleteTicketType } from '../../services/ticketTypes'
import { listEvents } from '../../services/events'
import { getCenterOffers } from '../../services/offers'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline'

const CenterTicketsPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeView, setActiveView] = useState('tickets') // tickets, ticket-types
  const [tickets, setTickets] = useState([])
  const [ticketTypes, setTicketTypes] = useState([])
  const [events, setEvents] = useState([])
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, EVENT, OFFER, ACTIVE, USED
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [formData, setFormData] = useState({
    eventId: '',
    name: '',
    price: '',
    quantity: '',
    description: ''
  })

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'verify', label: 'Verify Tickets', icon: QrCodeIcon, path: '/center/verify' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center/profile' }
  ]

  useEffect(() => {
    fetchTickets()
    fetchTicketTypes()
    fetchEvents()
    fetchOffers()
  }, [])

  const fetchTickets = async () => {
    setLoading(true)
    const res = await listCenterTickets()
    if (res.status === 200) {
      setTickets(res.data || [])
    }
    setLoading(false)
  }

  const fetchTicketTypes = async () => {
    const res = await listCenterTicketTypes()
    if (res.status === 200) {
      setTicketTypes(res.data || [])
    }
  }

  const fetchEvents = async () => {
    try {
      const res = await listEvents({ centerId: user?.id })
      console.log('Events response:', res)
      if (res.status === 200) {
        setEvents(res.data || [])
      } else {
        console.error('Failed to fetch events:', res.error)
      }
    } catch (error) {
      console.error('Error fetching events:', error)
    }
  }

  const fetchOffers = async () => {
    try {
      const res = await getCenterOffers({})
      console.log('Offers response:', res)
      if (res.status === 200) {
        setOffers(res.data || [])
      } else {
        console.error('Failed to fetch offers:', res.error)
      }
    } catch (error) {
      console.error('Error fetching offers:', error)
    }
  }

  const handleCreateTicketType = async (e) => {
    e.preventDefault()
    
    if (!formData.eventId) {
      setMessage('Please select an event')
      setMessageType('error')
      return
    }
    
    if (!formData.name || !formData.price || !formData.quantity) {
      setMessage('Please fill in all required fields')
      setMessageType('error')
      return
    }

    const payload = {
      eventId: formData.eventId,
      name: formData.name,
      price: parseFloat(formData.price),
      quantity: parseInt(formData.quantity),
      description: formData.description
    }

    console.log('Creating ticket type with payload:', payload)

    const res = await addTicketType(payload)
    console.log('Create ticket type response:', res)
    
    if (res.status === 200 || res.status === 201) {
      setMessage('Ticket type created successfully')
      setMessageType('success')
      setShowCreateModal(false)
      setFormData({ eventId: '', name: '', price: '', quantity: '', description: '' })
      fetchTicketTypes()
    } else {
      setMessage(res.error || 'Failed to create ticket type')
      setMessageType('error')
    }
    setTimeout(() => setMessage(''), 5000)
  }

  const handleDeleteTicketType = async (ticketTypeId) => {
    if (!window.confirm('Are you sure you want to delete this ticket type?')) return

    const res = await deleteTicketType(ticketTypeId)
    if (res.status === 200) {
      setMessage('Ticket type deleted successfully')
      setMessageType('success')
      fetchTicketTypes()
    } else {
      setMessage(res.error || 'Failed to delete ticket type')
      setMessageType('error')
    }
    setTimeout(() => setMessage(''), 5000)
  }

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: 'bg-green-100 text-green-700',
      USED: 'bg-gray-100 text-gray-700',
      TRANSFERRED: 'bg-blue-100 text-blue-700',
      VOID: 'bg-red-100 text-red-700'
    }
    return badges[status] || 'bg-gray-100 text-gray-700'
  }

  const getTypeBadge = (type) => {
    const badges = {
      EVENT: 'bg-purple-100 text-purple-700',
      OFFER: 'bg-emerald-100 text-emerald-700'
    }
    return badges[type] || 'bg-gray-100 text-gray-700'
  }

  const filteredTickets = tickets.filter(ticket => {
    // Filter by type/status
    let passesFilter = true
    if (filter !== 'all') {
      if (filter === 'EVENT' || filter === 'OFFER') {
        passesFilter = ticket.ticketType === filter
      } else {
        passesFilter = ticket.status === filter
      }
    }
    
    // Filter by search query
    if (searchQuery && passesFilter) {
      const query = searchQuery.toLowerCase()
      const matchesCode = ticket.code?.toLowerCase().includes(query)
      const matchesAttendee = ticket.attendeeId?.name?.toLowerCase().includes(query) || 
                              ticket.attendeeId?.email?.toLowerCase().includes(query)
      const matchesEvent = ticket.eventId?.title?.toLowerCase().includes(query)
      const matchesOffer = ticket.offerId?.title?.toLowerCase().includes(query)
      
      return matchesCode || matchesAttendee || matchesEvent || matchesOffer
    }
    
    return passesFilter
  })

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'tickets'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Tickets</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Tickets & Ticket Types</h1>
              <p className="text-slate-600 mt-1">Manage tickets and create ticket types for events</p>
            </div>
            {activeView === 'ticket-types' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-lg"
              >
                <PlusCircleIcon className="w-5 h-5" />
                Create Ticket Type
              </button>
            )}
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
              messageType === 'error' 
                ? 'border-red-200 bg-red-50 text-red-800' 
                : messageType === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-emerald-100 bg-emerald-50 text-emerald-800'
            }`}>
              {message}
            </div>
          )}

          {/* View Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-200">
            <button
              onClick={() => setActiveView('tickets')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeView === 'tickets'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Issued Tickets
            </button>
            <button
              onClick={() => setActiveView('ticket-types')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeView === 'ticket-types'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Ticket Types (Events)
            </button>
          </div>

          {/* Tickets View */}
          {activeView === 'tickets' && (
            <>
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by ticket code, attendee name, email, event or offer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p className="mt-2 text-sm text-slate-600">
                    Found {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''} matching "{searchQuery}"
                  </p>
                )}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2 mb-6">
                {['all', 'EVENT', 'OFFER', 'ACTIVE', 'USED'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === status
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {status === 'all' ? 'All Tickets' : status}
                  </button>
                ))}
              </div>

          {/* Tickets List */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
              <p className="mt-3 text-sm text-slate-500">Loading tickets...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-20">
              <TicketIcon className="mx-auto h-16 w-16 text-slate-300" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">No tickets found</h3>
              <p className="mt-2 text-sm text-slate-600">Tickets will appear here when customers purchase your events or offers</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Ticket Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Attendee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Issued At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Used At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredTickets.map(ticket => (
                      <tr key={ticket._id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <TicketIcon className="w-5 h-5 text-slate-400 mr-2" />
                            <span className="text-sm font-mono font-medium text-slate-900">{ticket.code}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getTypeBadge(ticket.ticketType)}`}>
                            {ticket.ticketType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">
                            {ticket.attendeeId?.name || 'N/A'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {ticket.attendeeId?.email || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusBadge(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {new Date(ticket.issuedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {ticket.usedAt ? new Date(ticket.usedAt).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
            </>
          )}

          {/* Ticket Types View */}
          {activeView === 'ticket-types' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {ticketTypes.length === 0 ? (
                <div className="text-center py-20">
                  <TicketIcon className="mx-auto h-16 w-16 text-slate-300" />
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">No ticket types found</h3>
                  <p className="mt-2 text-sm text-slate-600">Create ticket types for your events</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                  >
                    <PlusCircleIcon className="w-5 h-5" />
                    Create Ticket Type
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Event
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Remaining
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {ticketTypes.map(ticketType => (
                        <tr key={ticketType._id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">{ticketType.name}</div>
                            {ticketType.description && (
                              <div className="text-xs text-slate-500">{ticketType.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {ticketType.eventId?.title || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">
                            {ticketType.currency || 'NGN'} {ticketType.price?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {ticketType.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {ticketType.remaining}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              ticketType.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {ticketType.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleDeleteTicketType(ticketType._id)}
                              className="text-red-600 hover:text-red-800"
                              disabled={ticketType.remaining < ticketType.quantity}
                              title={ticketType.remaining < ticketType.quantity ? 'Cannot delete ticket type with sold tickets' : 'Delete'}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Create Ticket Type Modal */}
          {showCreateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-900">Create Ticket Type</h2>
                    <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateTicketType} className="space-y-4">
                    {/* Info Box */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="text-xs text-emerald-800">
                        <strong>Note:</strong> Ticket types are for events only. Offers manage their quantity directly and don't need ticket types.
                      </p>
                    </div>

                    {/* Event Selection */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Select Event <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.eventId}
                        onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      >
                        <option value="">Select an event</option>
                        {events.map(event => (
                          <option key={event._id} value={event._id}>
                            {event.title}
                          </option>
                        ))}
                      </select>
                      {events.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          No events found. Please create an event first.
                        </p>
                      )}
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Ticket Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="e.g., VIP, General Admission"
                        required
                      />
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="5000"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="100"
                        min="1"
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Optional description..."
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(false)}
                        className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        Create
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default CenterTicketsPage

